import type { CSSProperties } from 'react';
import {
  RELEASE_THEMES,
  ReleaseKitLook,
  ReleaseThemeId,
  ThemeCaptionStyle,
  themeType,
} from 'fym-shared/src/music24/releaseThemes';
import { themeStillVars } from '../../themes/themeStillVars';

/** The song facts that a drawn frame shows. */
export interface FrameSong {
  artworkUrl: string | undefined;
  title: string;
  artist: string;
  /** "Out now" or "Pre-save now". */
  kicker: string;
}

/** The sung line of a lyric frame with the line before and the next line (design s2, s4). */
export interface FrameLyric {
  prev: string;
  current: string;
  next: string;
}

/**
 * The CSS variables of a frame (design s, `vars`): the colours, the cover and the display type of
 * the theme still (`themeStillVars`), plus the text type of the theme (weight, case, tracking) for
 * the artist line and the dimmed lyric lines. Pure.
 */
export function assetFrameVars(
  look: ReleaseKitLook,
  palette: readonly string[],
  artworkUrl: string | undefined
): CSSProperties {
  const base = themeStillVars({
    themeId: look.themeId,
    fontOverride: look.fontOverride,
    palette,
    artworkUrl,
    title: '',
    layout: 'card',
  });
  const { text } = themeType(look);
  return {
    ...base,
    '--tw': text.weight,
    '--tc': text.case,
    '--tl': `${text.tracking}em`,
  } as CSSProperties;
}

/** The caption style of a theme: how a lyric line comes in (line, word or kinetic). */
export function frameCaptionStyle(themeId: ReleaseThemeId): ThemeCaptionStyle {
  return RELEASE_THEMES[themeId].brief.captionStyle;
}

/**
 * The lyric of the frames: the line at the hook of the song with its neighbours, else the first
 * lines of the lyrics. With no lyrics, the title takes the place of the sung line. Pure.
 */
export function frameLyricOf(lines: readonly string[], currentIndex: number, title: string): FrameLyric {
  if (lines.length === 0) {
    return { prev: '', current: title, next: '' };
  }
  const index = Math.min(Math.max(currentIndex, 0), lines.length - 1);
  return { prev: lines[index - 1] ?? '', current: lines[index] ?? title, next: lines[index + 1] ?? '' };
}
